import {ActionContext} from "vuex";
import {IAppState} from "@/interfaces/app-state.interface";
import {Vue} from "vue-property-decorator";
import {ICarList} from '@/modules/CarList/interfaces/car-list.interface';
import {carListResources} from "@/modules/CarList/resources/car-list.resources";
import {ICar} from "@/modules/Car/interfaces/car.interface";
import moment from "moment";
import {IApp} from "@/interfaces/app.interface";
import {httpResource} from "@/resources/http.resource";
import {ICarUnion} from "@/modules/CarList/interfaces/car-union.interface";
import {bus} from "@/plugins/bus";
import {IBook} from "@/interfaces/book.interface";

type CarListStore = ActionContext<ICarList.IState, IAppState>;

export default {
  namespaced: true,

  state: {
    carList: [],
    filters: {},
    textInfo: '',
    currentFilters: {},
    payGuestHoursFlag: true,
    activeSortedType: ICarList.SortedType.OwnerByAlphabet,
    sortDirectionUp: true,
    displayFilters: false,
    activeListingMode: ICarList.ListingDisplayMode.Cards,
    carUnionList: [],
    loading: false,
  },

  getters: {
    sortedList: (state: ICarList.IState, getters, rootState: IAppState) => {
      let withoutValues = [];
      let withValues = [];
      let result = [];
      let books = {
        [ICarList.SortedType.CarStatusId]: rootState.book.books[IBook.BookName.CarStatuses],
      };

      state.carList.forEach(car=> {
        if (state.activeSortedType === ICarList.SortedType.TimeOnParking) {
          if (car.timeHour || car.timeMinute || car.timePark === '00:00') withValues.push(car);
          else withoutValues.push(car);
        }
        else {
          if (!car[state.activeSortedType] || car[state.activeSortedType] === ' ') withoutValues.push(car);
          else withValues.push(car);
        }
      });

      withValues.sort((a: ICar.IEntity, b: ICar.IEntity ) => {
        let aValue: any = null;
        let bValue: any = null;

        switch (state.activeSortedType) {
          case ICarList.SortedType.TimeOnParking:
            aValue = (a.timeHour ? a.timeHour * 60 : 0) + (a.timeMinute ? a.timeMinute : 0);
            bValue = (b.timeHour ? b.timeHour * 60 : 0) + (b.timeMinute ? b.timeMinute : 0);
            break;

          case ICarList.SortedType.DateBeg:
          case ICarList.SortedType.DateIn:
          case ICarList.SortedType.DateOut:
            aValue = moment(a[state.activeSortedType], IApp.DateFormats.FromServer).format('x');
            bValue = moment(b[state.activeSortedType], IApp.DateFormats.FromServer).format('x');
            break;

          case ICarList.SortedType.TmPersonId:
          case ICarList.SortedType.DaysOnParking:
          case ICarList.SortedType.Balance:
            aValue = Number(a[state.activeSortedType]);
            bValue = Number(b[state.activeSortedType]);
            break;

          case ICarList.SortedType.CarStatusId:
            let aBookItem = books[state.activeSortedType].find(item => item.id === a[state.activeSortedType]);
            let bBookItem = books[state.activeSortedType].find(item => item.id === b[state.activeSortedType]);
            aValue = aBookItem ? aBookItem.name : a[state.activeSortedType];
            bValue = bBookItem ? bBookItem.name : b[state.activeSortedType];
            break;

          default:
            aValue = a[state.activeSortedType].toString().trim().toLowerCase();
            bValue = b[state.activeSortedType].toString().trim().toLowerCase();
            break;
        }

        return state.sortDirectionUp
          ? aValue > bValue ? 1 : -1
          : aValue < bValue ? 1 : -1;
      });

      if (state.activeSortedType === ICarList.SortedType.OwnerByAlphabet) {
        result = [...withValues, ...withoutValues];
      }
      else {
        result = [...withValues, ...withoutValues];
      }
      return result;
    },
    mainFilterEmpty: (state: ICarList.IState) => {
      return !Boolean(state.filters.fltCorpId) &&
        !Boolean(state.filters.fltCarTypes) &&
        !Boolean(state.filters.fltStatusId) &&
        !Boolean(state.filters.fltStr) &&
        !Boolean(state.filters.date);
    },
  },

  mutations: {
    setProperty(state: ICarList.IState, { name, value }: { name: string, value: any}) { Vue.set(state, name, value); },
    startLoading(state: ICarList.IState) { state.loading = true },
    stopLoading(state: ICarList.IState) { state.loading = false },

    setFilterProp(state: ICarList.IState, { name, value }: { name: string; value: any; }) {
      Vue.set(state.currentFilters, name, value);
    },

    setFiltersFromServer(state: ICarList.IState, filters: any) {
      let ds = moment(filters.fltDateBeg, 'YYYY-MM-DD HH:mm:ss').format(IApp.DATE_TIME_FORMAT_ELEMENT_UI);
      let de = moment(filters.fltDateEnd, 'YYYY-MM-DD HH:mm:ss').format(IApp.DATE_TIME_FORMAT_ELEMENT_UI);
      const date = [ds, de];
      delete filters.fltDateBeg;
      delete filters.fltDateEnd;
      const result = {...filters};
      if (!!date[0] && !!date[1]) result.date = date;
      Vue.set(state, 'filters', result);
    },

    setFiltersFromCarUnion(state: ICarList.IState, filters: any) {
      let ds = moment(filters.dateBeg, 'YYYY-MM-DD HH:mm:ss').format(IApp.DATE_TIME_FORMAT_ELEMENT_UI);
      let de = moment(filters.dateEnd, 'YYYY-MM-DD HH:mm:ss').format(IApp.DATE_TIME_FORMAT_ELEMENT_UI);
      const date = [ds, de];
      const result = {
        fltCorpId: filters.corpId,
        fltStr: filters.commonFilter,
        fltStatusId: filters.statusId,
        fltParkId: filters.parkId,
        date,
      };
      Vue.set(state, 'filters', result);
    },

    addCarInList(state: ICarList.IState, car: ICar.IEntity) { state.carList.push(car) },
    updateCarInList(state: ICarList.IState, payload: { car: ICar.IEntity; carListPage: boolean; }) {
      let index = state.carList.findIndex(stateCar => stateCar.carId === payload.car.carId);
      if (index !== -1 && payload.carListPage) {
        // if ((payload.car.carStatusId === ICar.CarStatusId.Archive && state.filters.fltStatusId !== ICar.CarStatusId.Archive) ||
        //     (payload.car.carStatusId !== ICar.CarStatusId.Archive && state.filters.fltStatusId === ICar.CarStatusId.Archive)) {
        //   state.carList.splice(index, 1);
        // }
        // else {
          Vue.set(state.carList, index, payload.car);
        // }
      }
      else {
        Vue.set(state.carList, payload.carListPage ? state.carList.length : 0, payload.car);
      }
    },
    removeCarFromList(state: ICarList.IState, carId: number) {
      let index = state.carList.findIndex(stateCar => stateCar.carId === carId);
      if (index) state.carList.splice(index, 1);
      if (index === 0) state.carList.shift();
    },

    addNewCarUnion(state: ICarList.IState) { state.carUnionList.unshift({...ICarUnion.DefaultCarUnion}); },
    clearNewCarUnion(state: ICarList.IState) { state.carUnionList.shift(); },
    updateCarUnion(state: ICarList.IState, payload: { index: number; carUnion: ICarUnion.IEntity}) { Vue.set(state.carUnionList, payload.index, {...payload.carUnion}) },
  },

  actions: {
    async init({ state, rootState, commit, dispatch }: CarListStore) {
      commit('startLoading');

      let listingModeStorage = localStorage.getItem(ICarList.LocalStorageKeys.ControlsDisplayMode);
      if (listingModeStorage === ICarList.ListingDisplayMode.EnterTable && rootState.auth.userAccess && !rootState.auth.userAccess.scCarsUnion) {
        listingModeStorage = ICarList.ListingDisplayMode.Cards;
      }
      commit('setProperty', { name: 'activeListingMode', value: listingModeStorage || ICarList.ListingDisplayMode.Cards });
      try {
        let command = ICarList.Commands.Get;

        // Считывание параметров фильтра из урла.
        let carUnionMode = state.activeListingMode === ICarList.ListingDisplayMode.EnterTable;
        let routeQuery = rootState.route.query;
        if (routeQuery && routeQuery.filters) {
          commit('setProperty', { name: 'currentFilters', value: JSON.parse(routeQuery.filters) });
          command = ICarList.Commands.Set;
        }
        const filtersToSend: any = ICarList.PrepareFilters(state.currentFilters, carUnionMode);

        const params: any = { command, ...filtersToSend };
        let action = state.activeListingMode === ICarList.ListingDisplayMode.EnterTable ? ICarList.Action.CarsUnionMake : ICarList.Action.CarsMake;
        const response = await carListResources.httpAction({ action, params });

        if (state.activeListingMode === ICarList.ListingDisplayMode.EnterTable) {
          commit('setProperty', { name: 'carUnionList', value: response.data.result.report });
          if (response.data.result.filter) commit('setFiltersFromCarUnion', response.data.result.filter);
        }
        else {
          const carList = ICarList.PrepareCarList(rootState, response.data.result.Cars);
          commit('setProperty', {name: 'carList', value: carList });
          commit('setProperty', {name: 'textInfo', value: response.data.result.textInfo});
          if (response.data.result.filter) commit('setFiltersFromServer', response.data.result.filter);
          commit('setProperty', {name: 'payGuestHoursFlag', value: response.data.result.payGuestHoursFlag});
        }
      }
      catch (error) { dispatch('handleError', error, { root: true }); }
      commit('stopLoading');
    },

    async getCar({ rootState, state, commit, dispatch }: CarListStore, data: { carNumber: string }) {
      commit('startLoading');
      try {
        const params: any = {
          command: ICarList.Commands.Get,
          fltStr: data.carNumber,
        };
        const { data: { result: response }} = await httpResource.httpAction({ action: ICarList.Action.CarsMake, params });
        const carList = ICarList.PrepareCarList(rootState, response.Cars);
        commit('setProperty', { name: 'carList', value: [...state.carList, ...carList ] });
        commit('setProperty', {name: 'payGuestHoursFlag', value: response.payGuestHoursFlag});
      }
      catch (error) { dispatch('handleError', error, { root: true }); }
      commit('stopLoading');
    },

    async reloadCar({ rootState, state, commit, dispatch }: CarListStore, payload: { carId: number }) {
      try {
        const params: any = {
          command: ICarList.Commands.Get,
          carId: payload.carId,
        };
        const { data: { result: { Cars: list } }} = await httpResource.httpAction({ action: ICarList.Action.CarsMake, params });
        const carList = ICarList.PrepareCarList(rootState, list);
        commit('updateCarInList', { car: carList[0], carListPage: rootState.route.name === IApp.ROUTE_NAME.PAGE_CAR_LIST });
      }
      catch (error) { dispatch('handleError', error, { root: true }); }
    },

    async setFilters({ rootState, state, commit, dispatch }: CarListStore) {
      commit('startLoading');
      let asyncAction = async () => {
        let carUnionMode = state.activeListingMode === ICarList.ListingDisplayMode.EnterTable;
        const filtersToSend: any = ICarList.PrepareFilters(state.currentFilters, carUnionMode);
        let action = carUnionMode ? ICarList.Action.CarsUnionMake : ICarList.Action.CarsMake;
        const params: any = { command: ICarList.Commands.Set, ...filtersToSend };
        const response = await carListResources.httpAction({ action, params });

        if (state.activeListingMode === ICarList.ListingDisplayMode.EnterTable) {
          commit('setProperty', { name: 'carUnionList', value: response.data.result.report });
          commit('setFiltersFromCarUnion', response.data.result.filter);
        }
        else {
          const carList = ICarList.PrepareCarList(rootState, response.data.result.Cars);
          commit('setProperty', {name: 'carList', value: carList});
          commit('setProperty', {name: 'textInfo', value: response.data.result.textInfo});
          commit('setFiltersFromServer', response.data.result.filter);
        }
      };

      await dispatch('defaultResourceAction', { asyncAction }, { root: true });
      commit('stopLoading');
    },

    async actionReportMovements({ state, commit, dispatch }: CarListStore, data: { command: ICarList.CommandsMovements, form: any }) {
      commit('startLoading');
      let asyncAction = async () => {
        const params: any = { command: data.command, ...data.form };
        await carListResources.httpActionMovements({ params });
      };

      let response = await dispatch('defaultResourceAction', { asyncAction }, { root: true });
      commit('stopLoading');
      return response.success;
    },

    async saveCarsUnion({ state, commit, dispatch }: CarListStore, payload: { formModel: any; index: number }) {
      commit('startLoading');
      let asyncAction = async () => {
        let formToSend = {...payload.formModel};
        Object.keys(payload.formModel).forEach(prop => {
          if (!payload.formModel[prop]) formToSend[prop] = null;
        });
        const params: any = {
          command: formToSend.corpId ? ICarList.Commands.UpdateUnion : ICarList.Commands.Add,
          ...formToSend,
        };
        const response = await httpResource.httpAction({action: ICarList.Action.CarsUnionMake, params});
        let index = payload.index;
        if (index === 0 && !formToSend.corpId && !state.carUnionList[0].corpId && state.carUnionList[1] && !state.carUnionList[1].corpId) {
          index = 1;
        }
        commit('updateCarUnion', { carUnion: response.data.result.report[0], index });
      };

      let response = await dispatch('defaultResourceAction', { asyncAction }, { root: true });
      commit('stopLoading');
      return response.success;
    },

    async addNewCarUnion({ state, commit, dispatch }: CarListStore) {
      // if (state.carUnionList.length && state.carUnionList[0].corpId) {
        commit('addNewCarUnion');
        setTimeout(() => bus.$emit(ICarUnion.EmitterEvents.ActivateNewRow, 0));
      // }
      // else {
      //   setTimeout(() => bus.$emit(ICarUnion.EmitterEvents.ActivateNewRow, 0));
      // }
    },
  }
};
